import {
  CouvertureDto,
  FileTypeEnum,
  OrderStatusEnum,
} from 'src/common/interfaces'
import {
  AccountDto,
  DoctorDto,
  Document,
  Exam,
  ExamDto,
  ExamRoom,
  GedFile,
  GedFileDto,
  LabWorkstation,
  MedicalOrder,
  MedicalOrderStatusEnum,
  MemberDto,
  PaginatedQuery,
  PatientDto,
  PatientTransportDto,
  RppsDto,
  SexEnum,
  SiteDto,
  BodyPartEnum,
  AgeRangeEnum,
} from './api'
import { FunctionalUnitDTO } from './functionalUnitService'
import type { RequestingOrganisationDto } from './RequestingOrganisationsService'

export interface CreateMedicalOrderDto {
  siteId: string
  attendingDoctor: RppsDto | null
  referringDoctor: RppsDto | null
  consultingDoctor: RppsDto | null
  registerDate?: Date
  registerTime?: string
  comments?: string
  patientId: number
  isUrgent?: boolean
  isNightShift?: boolean
  isHoliday?: boolean
}

export interface CCAMDto {
  id: number
  code: string
  libelle: string
  codeRegroupement: string
  prixOptam: number
  prixNonOptam: number
  modificateurs: string
}
export interface AppointmentMinDTO {
  start: Date
  end: Date
  order: OrderDto
}
export interface PatientStats {
  sold: number
  examCount: number
  noShows: AppointmentMinDTO[]
  noShowsCount: number
}

export interface NGAPDto {
  id: number
  code: string
  libelle: string
  prix: number
  dateDebut: Date
  dateFin: Date | null
}

export interface FindCCAMQuery {
  code?: string
  label?: string
  examDate: Date
}

export interface FindManyCCAMQuery {
  codes: string[]
  examDate: Date
}

export interface CreatePrecotationDto {
  id: number
  association?: number
  cotation_type: string
  cotation: string
  modificateur: string
  date_execution: string
  prix_unitaire: number
  code_entente_prealable: string
  date_entente_prealable: string
  id_medical_order: number
  m1: string
  m2: string
  m3: string
  m4: string
}

export interface PrecotationDto {
  id?: number
  identifier?: string
  cotation_type: string
  cotation: string
  modificateur?: string
  date_execution?: string
  prix_unitaire: number
  code_entente_prealable?: string
  date_entente_prealable?: string
  id_medical_order?: number
  association?: number
  m1?: string
  m2?: string
  m3?: string
  m4?: string
}

export type FindExamFromBillingQuery = {
  CodeCcam?: string
  CodeNgap?: string
  SiteId: number
}
export interface SearchCCAMQuery {
  libelle: string
  examDate: Date
}

export interface SearchNGAPQuery {
  libelle: string
  examDate: Date
}

interface CommentDto {
  id: number
  orderId: number
  content: string
  createdBy: number
  createdAt: Date
}

export interface OrderDto {
  id: number
  site: SiteDto
  patient: PatientDto
  exam: ExamDto
  createdAt: Date
  siteId: number
  referringDoctor: DoctorDto
  attendingDoctor: MemberDto
  consultingDoctor: MemberDto
  medicalOrders: MedicalOrder[]
  secretary: MemberDto
  registeredAt?: Date
  isEmergency?: boolean
  isNightShift?: boolean
  isHoliday?: boolean
  astreinte?: boolean
  commentsCount: number
  documentsCount: number
  children: OrderDto[]
  parentId: number | null
  plannedDate: Date
  documents?: Document[]
  visit?: VisitCouvertureDto
  visitId?: number
  previousVisits?: VisitDTO[] // Added this to simulate the data for now
  deletedAt?: Date
  aldStatus: AldStatusEnum
  patientTransport?: PatientTransportDto
  patientTransportId?: number
  accommodationFunctionalUnit?: FunctionalUnitDTO
  medicalFunctionalUnit?: FunctionalUnitDTO
}
export enum DictationStatus {
  AWAITING_TRANSCRIPTION = 'AWAITING_TRANSCRIPTION',
  TRANSCRIBING = 'TRANSCRIBING',
  TRANSCRIBED = 'TRANSCRIBED',
}
export interface PatientAldDTO {
  id: string
  libelle: string
  code: string
  etat: string
  dateFin?: string
  cimCode?: string
  source: 'ALDi' | 'User'
  comment?: string
  patientId: number
  createdBy: MemberDto
  createdAt: Date
}

export interface FindOrdersQuery extends PaginatedQuery {
  search?: string
  siteId?: number
  status?: OrderStatusEnum
  startDate?: string
  endDate?: string
  patientId?: number
  patientIds?: number[]
  hasRadiologyStudy?: boolean
}

export interface UpdateOrderDto {
  id?: number
  siteId?: number
  patientId?: number
  examId?: number
  status?: MedicalOrderStatusEnum
  plannedDate?: Date
  cancelDate?: Date
  description?: string
  studyInstanceUID?: string
  accessionNumber?: string
  service?: string
  attendingDoctorId?: number
  referringDoctorId?: number
  consultingDoctorId?: number
  attendingDoctorName?: string
  consultingDoctorName?: string
  referringDoctorName?: string
  patientTransportId?: number
  registerDate?: string | null
  registerTime?: string | null
  isEmergency?: boolean
  isNightShift?: boolean
  isHoliday?: boolean
  astreinte?: boolean
  attendingDoctor?: MemberDto | null
  referringDoctor?: RppsDto | null
  consultingDoctor?: MemberDto | null
  familyDoctor?: RppsDto | null
  comment?: Partial<Pick<CommentDto, 'id' | 'content' | 'createdBy'>>
  titulaireMemberId?: number
  secretaryId?: number
  deletedAt?: Date | null
  billStatus?: BillStatus
}

export enum TypeActe {
  NGAP = 'NGAP',
  CCAM = 'CCAM',
}

export interface PrecotValidationActItem {
  indicateurForcageAmo: string
  flagRemboursementExceptionnel: string
  supplementCharge: string
  numPrestation: number
  codeActe: string
  codeActivite: number
  codePhaseTraitement: number
  modificateurs: string[]
  codeAssociation: number
  lieuExecution: string
  identifiantLieuExecution: string
  typeActe: TypeActe
}

export interface ValidatePrecotationDto {
  idMemberSituation: number
  actes: PrecotValidationActItem[]
  dateApplication: Date
}

export interface CotationValidationError {
  codeErreur: number
  codeRegle: string
  messageErreur: string
  forcable: boolean
  acte: string
}

export interface FindVisitQuery extends PaginatedQuery {
  search?: string
  siteId?: number
}

export interface VisitDTO {
  id: number
  patient: PatientDto
  siteId: number
  site: SiteDto
  admissionDate: Date
  dischargedDate: Date
  hospitalService: string
  couverture?: CouvertureDto
  createdAt: Date
  orders: OrderDto[]
  updatedAt: Date
  referringDoctor: DoctorDto
  medicalFunctionalUnit?: FunctionalUnitDTO
  accomodationFunctionalUnit?: FunctionalUnitDTO
  accomodationPreference?: AccomodationPreference
  requestingOrganisationId?: string
  requestingOrganisation?: VisitRequestingOrganisationDto
}

export interface VisitCouvertureDto {
  id: number
  coverageId: number
  createdAt: Date
  requestingOrganisation?: VisitRequestingOrganisationDto
  identifier: string
}
export interface VisitRequestingOrganisationDto {
  id: string
  name: string
  siteId?: number
  city?: string
}

export interface UpdateVisitDTO {
  admissionDate?: Date
  dischargedDate?: Date
  hospitalService?: string
  patientId?: number
  siteId?: number
  medicalOrderIds?: number[]
  accomodationFunctionalUnitId?: number
  medicalFunctionalUnitId?: number
  accomodationPreference?: AccomodationPreference
  requestingOrganisationId?: string | null
}


export interface AccomodationPreference {
  single: boolean
  tv: boolean
}

export interface CreateVisitDTO {
  patientId: number
  siteId: number
  admissionDate?: Date
  dischargedDate?: Date
  hospitalService?: string
  referringDoctor?: RppsDto
  accomodationFunctionalUnitId?: number
  accomodationPreference?: AccomodationPreference
  medicalFunctionalUnitId?: number
  requestingOrganisationId?: string | null
}

export enum HospitalUnitType {
  MEDICAL = 'Unité médicale',
  MEDICO_TECHNICAL = 'Unité médico-technique',
  MEDICO_SOCIAL = 'Unité médico-sociale / Logistique soins',
  ADMINISTRATIVE = 'Unité administrative',
  TECHNICAL_SUPPORT = 'Unité technique / support',
  ACCOMMODATION = 'Unité d\'hébergement',
}

export enum MedicalUnitSubtype {
  HOSPITALIZATION = 'Hospitalisation complète',
  OUTPATIENT = 'Consultations externes',
  DAY_HOSPITAL = 'Hôpital de jour',
  CRITICAL_CARE = 'Soins critiques (Réanimation, USC)',
  MATERNITY = 'Maternité / Néonatalogie',
}

export enum MedicoTechnicalUnitSubtype {
  IMAGING = 'Imagerie médicale (Radio, Scanner, IRM, Médecine nucléaire)',
  LAB = 'Laboratoire de biologie médicale',
  PHARMACY = 'Pharmacie à usage intérieur',
  OPERATING_ROOM = 'Bloc opératoire',
  PATHOLOGY = 'Anatomopathologie / Hygiène hospitalière',
  STERILIZATION = 'Stérilisation',
}

export enum MedicoSocialUnitSubtype {
  REHAB = 'Soins de suite et réadaptation (SSR)',
  EHPAD = 'EHPAD rattaché',
  HOMECARE = 'Hospitalisation à domicile (HAD)',
  LOGISTICS_CARE = 'Logistique médicale (brancardage, restauration patients)',
}

export enum AdministrativeUnitSubtype {
  GENERAL_MANAGEMENT = 'Direction générale',
  FINANCE = 'Finances / Facturation (GAM/GEF)',
  HR = 'Ressources humaines / Paie',
  IT = 'Systèmes d\'information (DSI)',
  LEGAL = 'Affaires juridiques / Qualité / Communication',
  PROCUREMENT = 'Services économiques / Achats',
}

export enum TechnicalSupportUnitSubtype {
  BIOMEDICAL = 'Maintenance biomédicale',
  FACILITY = 'Services techniques (bâtiments, énergie, sécurité)',
  LOGISTICS = 'Logistique générale (transport, magasin, restauration personnel)',
  TELECOM = 'Informatique / Télécom',
}

export enum AccommodationUnitSubtype {
  SINGLE_ROOM = 'Chambre individuelle',
  DOUBLE_ROOM = 'Chambre double',
  MULTI_BED_ROOM = 'Chambre collective',
  ISOLATION_ROOM = 'Chambre d\'isolement',
  INTENSIVE_CARE_BED = 'Lit de réanimation',
  RECOVERY_ROOM = 'Salle de réveil',
}

export interface CreatePatientDTO {
  sex: SexEnum
  firstName: string | null
  lastName: string | null
  motherMaidenName: string | null
  birthDate?: Date | null
  phoneNumber?: string | null
  email?: string | null
  address?: string | null
  placeOfBirth?: string | null
  ssn?: string | null
  weight?: number | null
  height?: number | null
  familyDoctor?: RppsDto | null
  birthDateStr?: string | null
  isForeigner?: boolean
  identifier?: string
  // comments: string
  // iod: string
  // ins: string
}

export interface PatientBrifDTO {
  id: string
  sex: SexEnum
  firstName: string
  lastName: string
  maidenName: string
  birthDate?: Date | null
  phoneNumber: string
  email: string
  address: string
  birthDatestring: string
  birthPlace: string
  birthPlaceDepartment: string
  ssn: string
  weight: number
  height: number
  familyDoctor: RppsDto
  comments: string
  iod: string
  ins: string
}

export interface FindPatientCoverageQuery {
  idVisit?: number
  idPatient?: number
}

export interface FindDoctorsQuery {
  search?: string
  city?: string
  rppsNumber?: string
  page: number
  limit: number
}

export interface AddressDto {
  id: number
  line1: string
  line2: string
  postalCode: string
  city: string
  country: string
  locationName: string
  locationType: string
  finessNumber: string
}

export interface CreateRecipientsDTO {
  orderId?: number
  visitId?: number
  doctorId?: number
  memberId?: number
  selectedAddressId?: number
  selectedEmail?: string
  receiveByEmail?: boolean
  receiveByPost?: boolean
}

export interface RecipientDTO {
  id: number
  doctor?: DoctorDto
  member?: MemberDto
  createdAt: Date
  selectedAddressId?: number
  selectedAddress?: AddressDto
  selectedEmail?: string
  receiveByEmail?: boolean
  receiveByPost?: boolean
}

export interface CreateDocumentDto {
  url: string
  filename: string
  fileType: FileTypeEnum
  mimeType: string
  relatedEntity?: number
  entityName?: string
  data?: string
}

export interface ActeDTO {
  codeRegroupement: string
  libelleActe: string
  montantPrixUnitaire: number
  montantBaseRemboursement: number
  montantDepassement: number
  isSupplement: boolean
  numPrestation: number
  codeActe: string
  codeActivite: number
  codePhaseTraitement: number
  modificateurs: any[]
  codeAssociation: number
  lieuExecution: string
  identifiantLieuExecution: string
}

interface SituationPsDTO {
  codeSpecialite: string
  codeConventionel: string
  identFacturation: string
  cleIdentFacturation: string
  contratPs: number
}

interface VisiteBillingDTO {
  montantTotal: number
  montantDepassement: number
  actes: ActeDTO[]
  situationPs: SituationPsDTO
  dateApplication: string
}

export interface PrecotationValidationResponse {
  visite: VisiteBillingDTO
  erreurs: any[]
}

export interface CreateBillDTO {
  idResip: number
  idLecteur: string
  idVisit: number
  medicalOrderIds: number[]
  securisationFacture: string
  apcv: boolean
  idOrder: number
}

export interface CreateInvoiceOnlyDto {
  idVisit: number
  medicalOrderIds: number[]
  idOrder: number
  idMemberSituation: number
}

export interface DocumentDTO {
  id: number
  fileId: number
  medicalOrderId: number
  fileType: FileTypeEnum
  gedFile: GedFile
  url: string
  createdAt: Date
}
export interface UpdateDocumentDTO {
  entityId?: number
  entityName: 'patient' | 'order' | 'visit'
}

export interface ExonerationDto {
  id: number
  code: string
  libelle: string
}

export interface NatureDto {
  id: number
  code: string
  libelle: string
  code_b2: string
}

export enum AldStatusEnum {
  ACTIVE = 'ACTIVE',
  UNFINISHED = 'UNFINISHED',
  NOT_FOUND = 'NOT_FOUND',
}

export interface MemberSituationDTO {
  identifiant: number
  idJfse: number
  idMember: number
  idSite: number
  identifiantLogique: number
  typeIdentificationStructure: number
  numIdentificationStructure: string
  raisonSociale: string
  numIdentificationPSFacturation: string
  cleIdentificationPSFacturation: string
  codeConventionnel: string
  codeSpecialite: string
  codeZoneTarif: string
  codeZoneIK: string
  codeAgrement1: string
  codeAgrement2: string
  codeAgrement3: string
  habilitationSignerFSE: boolean
  habilitationSignerLOT: boolean
  modeExercice: number
  statusExercice: number
  secteurActivite: number
  jsonSituation: string
  contratOptam: number
  member: MemberDto
  billingCompanyId: number
}

export interface PaymentModeDto {
  id: number
  isActive: boolean
  typeIsMutable: boolean
  code: string
  label: string
  type: PaymentModeType
  accountingSectionCode: string
  isDefaultValue: boolean
  updatedAt: Date
  updatedBy: MemberDto
  createdBy: MemberDto
}

export interface CreatePaymentModeDto {
  code: string
  label: string
  type: PaymentModeType
  accountingSectionCode: string
}

export interface UpdatePaymentModeDto extends Partial<CreatePaymentModeDto> {
  isActive?: boolean
}

export enum PaymentModeType {
  CASH = 'CASH',
  CHEQUE = 'CHEQUE',
  CARD = 'CARD',
  FREE = 'FREE',
  LOSS_PROFIT = 'LOSS_PROFIT',
  REDISTRIBUTION = 'REDISTRIBUTION',
  DEBT_BALANCE = 'DEBT_BALANCE',
  BANK_TRANSFER = 'BANK_TRANSFER',
  AMEX = 'AMEX',
}

export interface BillingCompanyDto {
  id: number
  isActive: boolean
  code: string
  finessNumber: string
  label: string
  registrationNumber: string
  taxNumber: string
  address: string
  postalCode: string
  city: string
  country: string
  updatedAt: Date
  updatedBy: MemberDto
  createdBy: MemberDto
  site: SiteDto
  bankAccount: BankAccountDto
}

export interface CreateBillingCompanyDto {
  code: string
  finessNumber: string
  label: string
  registrationNumber: string
  taxNumber: string
  address: string
  postalCode: string
  city: string
  country: string
  siteId: number
  bankAccountId: number
}

export interface UpdateBillingCompanyDto
  extends Partial<CreateBillingCompanyDto> {
  isActive?: boolean
}

export interface BankEstablishmentDto {
  id: number
  isActive: boolean
  code: string
  label: string
  isDefaultValue: boolean
  updatedAt: Date
  updatedBy: MemberDto
  createdBy: MemberDto
}

export interface CreateBankEstablishmentDto {
  code: string
  label: string
}

export interface UpdateBankEstablishmentDto
  extends Partial<CreateBankEstablishmentDto> {
  isActive?: boolean
}

export interface BankAccountDto {
  id: number
  isActive: boolean
  code: string //eg: SGN
  name: string // eg: Société Générale Nice
  countryCode: string //eg: FR01
  bankCode: string //eg: 30000
  branchCode: string //eg: 10000
  accountNumber: string //eg: 00012345678
  controlKey: string //eg: 06
  swiftCode: string //eg: SOGEFRPP
  isDeletable: boolean
  siteIdsNonDeletable: number[]
  updatedAt: Date
  updatedBy: MemberDto
  createdBy: MemberDto
}

export interface CreateBankAccountDto {
  code: string
  name: string
  countryCode: string
  bankCode: string
  branchCode: string
  accountNumber: string
  controlKey: string
  swiftCode: string
}

export interface UpdateBankAccountDto extends Partial<CreateBankAccountDto> {
  isActive?: boolean
}

export interface BankTransfertDto {
  id: number
  createdAt: Date
  bankEstablishment: BankEstablishmentDto
}

export interface PatientPaymentDto {
  transactionType: TransactionType
  id: number
  invoiceId: number
  paymentMode: PaymentModeDto
  amount: string
  cheque: ChequeDto
  bankTransferPayment: BankTransfertDto
  drawerName: string
  updatedAt: Date
  updatedBy: MemberDto
  createdAt: Date
  createdBy: MemberDto
  comment: string
}

export interface CreatePatientPaymentDto {
  transactionType: TransactionType
  paymentModeId: number
  amount: string
  comment?: string
  // PaymentModeType.CHEQUE case
  chequeNumber?: string
  bankEstablishmentId?: number // + PaymentModeType.BANK_TRANSFER case
  drawerName?: string
  minimumRequestedDepositDate?: Date
  invoiceId: number
}

export interface ChequeDto {
  id: number
  chequeNumber: string
  drawerName: string
  minimumRequestedDepositDate: Date
  maximumDepositDate: Date
  bankEstablishment: BankEstablishmentDto
  unpaid: boolean
  patientPayment: PatientPaymentDto
  createdAt: Date
  updatedBy: MemberDto
}

export interface ChequeDepositDto {
  id: number
  createdAt: Date
  depositReference: string
  cheques: ChequeDto[]
  isLocked: boolean
  updatedAt: Date
  updatedBy: MemberDto
  createdBy: MemberDto
}

export interface CreateChequeDepositDto {
  depositReference: string
  chequeIds: number[]
}

export interface UpdateChequeDepositDto
  extends Partial<CreateChequeDepositDto> {
  isLocked?: boolean
}

export interface UpdateChequeDto {
  unpaid?: boolean
}

export interface DunningNoticeSendDto {
  id: number
  level: DunningNoticeLevel
  createdAt: Date
  createdBy: MemberDto
}

export interface OrganismeDto {
  libelle: string
  phoneNumber: string
  adress: string
}

export enum BillStatus {
  PAID = 'PAID',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
}

export interface InvoiceDto {
  id: number
  leftToRefund: number | null
  date: Date
  patient_total: number
  patient_payed: number
  patient_state: number
  total: number
  amo_total: number
  amo_payed: number
  amo_state: number
  amc_total: number
  amc_payed: number
  amc_state: number
  nextDunningNoticeLevelPatient: DunningNoticeLevel | null
  nextDunningNoticeLevelAmo: DunningNoticeLevel | null
  nextDunningNoticeLevelAmc: DunningNoticeLevel | null
  patient: Partial<PatientDto>
  dunningNoticeSends: DunningNoticeSendDto[]
  billing_companies: BillingCompanyDto
  patientPayments: PatientPaymentDto[]
  examLabels: string[]
  amoOrganisme: OrganismeDto
  amcOrganisme: OrganismeDto
  exams: { id: number; label: string }[]
  orderId: number
  type: number
  visit: { requestingOrganisation: RequestingOrganisationDto | null }
}

export interface UpdateInvoiceDto {
  patientPartLoss?: boolean
}

export interface PatientDetbDTO {
  patientId: number
  totalDebt: number
  numberOfInvoices: number
}

export enum InvoicePaymentStatus {
  TO_BE_PAID = 'TO_BE_PAID',
  NEED_DUNNING_NOTICE = 'NEED_DUNNING_NOTICE',
  OVERPAID = 'OVERPAID',
  FINALIZED = 'FINALIZED',
}

interface BaseQuotePrecotationDto {
  identifier: string
  cotation_type: string
  cotation: string
  modificateur?: string
  date_execution?: string
  prix_unitaire: number
  code_entente_prealable?: string
  date_entente_prealable?: string
  association?: number
  m1?: string
  m2?: string
  m3?: string
  m4?: string
}

export interface QuotePrecotationDto extends BaseQuotePrecotationDto {
  id: number
  exam: ExamDto
}

export interface QuoteDto {
  id: number
  isLocked: boolean
  createdAt: Date
  updatedAt: Date
  updatedBy: MemberDto
  createdBy: MemberDto
  patient: PatientDto
  quotePrecotations: QuotePrecotationDto[]
  attendingDoctor?: MemberDto
  attendingDoctorSite?: SiteDto
  billingCompany?: BillingCompanyDto
}

export interface CreateQuotePrecotationDto extends BaseQuotePrecotationDto {
  examId: number
}

export interface CreateQuoteDto {
  patientId: number
  precotations: CreateQuotePrecotationDto[]
  attendingDoctorId?: number
  attendingDoctorSiteId?: number
}

export interface UpdateQuoteDto extends Partial<CreateQuoteDto> {
  isLocked?: boolean
}

export enum DunningNoticeLevel {
  LEVEL_1 = 'LEVEL_1',
  LEVEL_2 = 'LEVEL_2',
  LEVEL_3 = 'LEVEL_3',
}

export interface UpdateDunningNoticeConfigDto {
  daysLevel1: number
  daysLevel2: number
  daysLevel3: number
  content1: string
  content2: string
  content3: string
}

export interface DunningNoticeConfigDto {
  id: number
  level: DunningNoticeLevel
  days: number
  content: string
  createdAt: Date
  updatedAt: Date
  updatedBy: MemberDto
}

export enum InvoiceEntity {
  PATIENT = 'PATIENT',
  AMC = 'AMC',
  AMO = 'AMO',
}

export enum TransactionType {
  PAYMENT = 'PAYMENT',
  REFUND = 'REFUND',
}

export interface LotDto {
  id: number
  typeLot: number
  numero: string
  dateLot: Date
  dateHeureTeletrans: Date
  montantTotal: number
  invoices: InvoiceDto[]
  organismPayment?: OrganismPaymentDto
}

export interface OrganismPaymentDto {
  id: number
  date: Date
  comment: string
  cheque: ChequeDto
  paymentMode: PaymentModeDto
  bankTransferPayment: BankTransfertDto
  updatedAt: Date
  updatedBy: MemberDto
  createdAt: Date
  createdBy: MemberDto
}

export interface CreateLotPaymentDto {
  date: Date
  paymentModeId: number
  comment?: string
  bankEstablishmentId: number
  // PaymentModeType.CHEQUE case
  chequeNumber?: string
  drawerName?: string
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface UpdateLotPaymentDto {}

export interface DocumentConfigDto {
  id: number
  logoUrl: string
  headerText: string
  headerImageUrl: string
  footerText: string
  footerImageUrl: string
}

export interface GetUrlByQRCodeDto {
  idResip: number
  idLecteur: string
  dateLecture: string
  typeLecture: number
  qrCode: string
}

export interface GetUrlByNFCDTO {
  idResip: number
  idLecteur: string
  dateLecture: string
  typeLecture: number
}

export interface GetUrlByContextDto {
  idResip: number
  idLecteur: string
}

export interface FindExamsQuery {
  search?: string
  siteIds?: number[]
}

export interface SesamReadType {
  code: number
  libelle: string
}

export interface MssProviderEmailDto {
  attachments: {
    contentType: string
    file: string
    fileName: string
    messageId: string
    part: number
    size: number
  }[]
  dateTime: string
  messageId: string
  folderId: string
  size: number
  addresses: {
    email: string
    name: string
    type: 'REPLY_TO' | 'FROM' | 'TO' | 'CC' | 'BCC'
  }[]
  body: string
  isHtml: boolean
  fromMessageId: string
  askReceiptAcknowledgement: boolean
  askReadAcknowledgement: boolean
  priority: 'HAUTE' | 'NORMAL'
  replyType: 'FORWARDED' | 'REPLIED'
  subject: string
  headers: Record<string, string>
  flags: (
    | 'ANSWERED'
    | 'DELETED'
    | 'DRAFT'
    | 'FLAGGED'
    | 'FORWARDED'
    | 'SEEN'
    | 'RECENT'
    | 'ATTACHMENT'
    | 'LOW_PRIORITY'
    | 'NONE'
    | 'PRIORITY'
    | 'REPLIED'
    | 'SENT_BY_ME'
    | 'UNREAD'
    | 'URGENT'
  )[]
}

export enum MssEmailStatus {
  TO_PROCESS = 'TO_PROCESS',
  PROCESSED = 'PROCESSED',
}

export interface MssRequestDto {
  id: number
  patient: Partial<PatientDto>
  documents: Document[]
  exams: ExamDto[]
  priority: MssRequestPriority
  priorityComment: string
  functionalUnit: FunctionalUnitDTO
  referringDoctor: DoctorDto
  site: SiteDto
  indication: string
  status: MssRequestStatus
  createdAt: Date
  updatedAt: Date
  updatedBy: MemberDto
  createdBy: MemberDto
}

export enum MssRequestPriority {
  URGENT = 'URGENT',
  NORMAL = 'NORMAL',
  LOW = 'LOW',
  SPECIFIC = 'SPECIFIC',
}

export enum MssRequestStatus {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  PENDING_DOCTOR_REVIEW = 'PENDING_DOCTOR_REVIEW',
}

interface MssAttachmentDto {
  date: Date
  type: FileTypeEnum
}

export interface CreateMssRequestDto {
  status: MssRequestStatus
  attachments: MssAttachmentDto[]
  priority: MssRequestPriority
  priorityComment: string
  indication: string
  examIds: number[]
  functionalUnitId: number
  referringDoctorId: number
  siteId: number
  patientId: number
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface UpdateMssRequestDto extends Partial<CreateMssRequestDto> {}

export interface MssEmailDto {
  id: number
  email: string
  active: boolean
  favorite: boolean
}

export interface IsotopeDto {
  id: string
  code: string
  name: string
  massNumber: number
  emissions: IsotopeEmissionType[]
  activityLevel: ActivityLevel.HIGH | ActivityLevel.LOW
  physicalHalfLifeS: number
  atomicNumber: number
  peakEnergyKev: number
  meanEnergyKev: number
  usages: IsotopeUsage[]
  notes?: string | null
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface CreateIsotopeDto {
  code: string
  name: string
  massNumber: number
  atomicNumber: number
  emissions: IsotopeEmissionType[]
  physicalHalfLifeS: number
  peakEnergyKev: number
  meanEnergyKev: number
  usages: IsotopeUsage[]
  notes?: string
}

export interface UpdateIsotopeDto extends Partial<CreateIsotopeDto> {
  isActive?: boolean
}

export enum CompoundType {
  PURE_ISOTOPE = 'PURE_ISOTOPE',
  READY_TO_USE = 'READY_TO_USE',
  TO_PREPARE = 'TO_PREPARE',
}

export interface CompoundDto {
  id: string
  name: string
  description: string
  type: CompoundType
  coldKit?: ProductCatalogBaseDto | null
  eluateMaxAgeMinutes?: number | null
  preparationMaxAgeMinutes?: number | null
  isActive: boolean
  isotopes: IsotopeDto[]
  productionSteps?: CompoundProductionStepDto[]
  createdAt: Date
  updatedAt: Date
}

export interface CreateCompoundDto {
  name: string
  description?: string
  isotopeIds?: string[]
  type: CompoundType
  coldKitId?: string | null
  eluateMaxAgeMinutes?: number | null
  enforceEluateMaxAge?: boolean // not used for backend call
  preparationMaxAgeMinutes?: number | null
  enforcePreparationMaxAge?: boolean // not used for backend call
}

export interface UpdateCompoundDto extends Partial<CreateCompoundDto> {
  isActive?: boolean
}

export enum StorageType {
  ROOM = 'ROOM',
  FRIDGE = 'FRIDGE',
  FREEZER = 'FREEZER',
  LAF = 'LAF',
  SHIELDED_CABINET = 'SHIELDED_CABINET',
  OTHER = 'OTHER',
}

export enum MonitoringMethod {
  NONE = 'NONE',
  MANUAL = 'MANUAL',
  DATALOGGER = 'DATALOGGER',
  BMS = 'BMS',
}

export interface StorageLocationDto {
  id: string
  name: string
  type: StorageType
  room: LabWorkstation
  capacityL?: number
  setpointC?: number
  rangeMinC?: number
  rangeMaxC?: number
  alarmLowC?: number
  alarmHighC?: number
  monitoring: MonitoringMethod
  isActive: boolean
  notes?: string
  createdAt: Date
  updatedAt: Date
  deletedAt?: Date
}

export interface CreateStorageLocationDto {
  name: string
  type: StorageType
  roomId: number
  capacityL?: number
  setpointC?: number
  rangeMinC?: number
  rangeMaxC?: number
  alarmLowC?: number
  alarmHighC?: number
  monitoring: MonitoringMethod
  notes?: string
}

export interface UpdateStorageLocationDto
  extends Partial<CreateStorageLocationDto> {
  isActive?: boolean
}

export enum SupplierPaymentMethod {
  CHEQUE = 'CHEQUE',
  TRANSFER = 'TRANSFER',
  SEPA_DIRECT_DEBIT = 'SEPA_DIRECT_DEBIT'
}

export interface SupplierCustomerSiteDto {
  id: string
  customerNumber: string
  site: SiteDto
}

export interface SupplierDto {
  id: string
  name: string
  type: SupplierType
  siret: string
  address?: string
  contacts: SupplierContact[]
  countryCode: string
  controlKey: string
  bankCode: string
  branchCode: string
  accountNumber: string
  swiftCode: string
  paymentMethods: SupplierPaymentMethod[]
  cyclotron: boolean
  productionSites: string[]
  customerSites: SupplierCustomerSiteDto[]
  isActive: boolean
  notes?: string
  createdAt: Date
  updatedAt: Date
  deletedAt?: Date
}

export interface SupplierContact {
  id: string
  email?: string
  phone?: string
  name?: string
  type: SupplierContactType
  createdAt: Date
}

export enum SupplierType {
  PRODUCT_SUPPLIER = 'PRODUCT_SUPPLIER',
  EXTERNAL_ORGANIZATION = 'EXTERNAL_ORGANIZATION',
  SOFTWARE_PUBLISHER = 'SOFTWARE_PUBLISHER',
  MACHINE_MANUFACTURER = 'MACHINE_MANUFACTURER',
  OFFICE_SUPPLIER = 'OFFICE_SUPPLIER',
  OTHER = 'OTHER',
}

export enum SupplierContactType {
  COMMERCIAL = 'COMMERCIAL',
  TECHNICIAN = 'TECHNICIAN',
  SALES_ADMINISTRATION = 'SALES_ADMINISTRATION',
  ACCOUNTING = 'ACCOUNTING',
  LOGISTICS = 'LOGISTICS',
  DELIVERY = 'DELIVERY',
  OTHER = 'OTHER',
}

export interface CreateSupplierContactDto {
  email?: string
  phone?: string
  name?: string
  type: SupplierContactType
}

export interface CreateSupplierCustomerSiteDto {
  customerNumber: string
  siteId: number
}

export interface CreateSupplierDto {
  name: string
  type: SupplierType
  siret: string
  address?: string
  contacts?: CreateSupplierContactDto[]
  countryCode?: string
  bankCode?: string
  branchCode?: string
  accountNumber?: string
  controlKey?: string
  swiftCode?: string
  paymentMethods?: SupplierPaymentMethod[]
  cyclotron?: boolean
  productionSites?: string[]
  customerSites?: CreateSupplierCustomerSiteDto[]
  approved?: boolean
  notes?: string
}

export interface UpdateSupplierDto extends Partial<CreateSupplierDto> {
  isActive?: boolean
}

export enum ProductType {
  GENERATOR = 'GENERATOR',
  KIT = 'KIT',
  READY_MADE = 'READY_MADE',
  REAGENT = 'REAGENT',
}

export interface ProductCatalogBaseDto {
  id: string
  name: string
}

export enum ProductPackageUom {
  UNIT = 'UNIT',
  PACK = 'PACK',
}

export type ProductValidationRuleOperator = 'AND' | 'OR'

type ProductCatalogValidation = {
  groupOperator?: ProductValidationRuleOperator
  rules: {
    role: string
    operator: ProductValidationRuleOperator
    members: MemberDto[]
  }[]
}

type CreateProductCatalogValidation = {
  groupOperator?: ProductValidationRuleOperator
  rules: {
    role: string
    operator: ProductValidationRuleOperator
    memberIds: string[]
  }[]
}

export interface ProductCatalogPriceDto {
  id: string
  priceExclVat: number
  vatRate: number
  startAt?: Date
  endAt: Date
}

export interface ProductCatalogDto extends ProductCatalogBaseDto {
  supplier: SupplierDto
  productCode: string
  type: ProductType
  genericName: string
  supplyLeadTimeDays?: number
  maxDivision?: number
  compound?: CompoundDto
  packageUom?: ProductPackageUom
  unitsPerPack?: number
  gtinEan?: string
  productFamily?: ProductFamilyDto
  requiresColdChain: boolean
  inUseStabilityMinCold?: number
  inUseStabilityMinRoom?: number
  excursionOutOfColdMaxMin?: number
  priceExclVat: number
  vatRate: number
  priceExpired: boolean
  prices: ProductCatalogPriceDto[]
  exams: ExamDto[]
  orderableIfExamAssociated?: boolean
  canBeOrderedDueToFutureExam?: boolean
  notes?: string
  validation?: ProductCatalogValidation
  generatorParentIsotope?: IsotopeDto
  generatorDaughterIsotope?: IsotopeDto
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface CreateProductCatalogPriceDto {
  priceExclVat: number
  vatRate: number
  startAt?: string
  endAt: string
}

export interface CreateProductCatalogDto {
  supplierId: string
  productCode: string
  name: string
  genericName: string
  supplyLeadTimeDays?: number
  maxDivision?: number
  type: ProductType
  compoundId?: string | null
  packageUom?: ProductPackageUom
  unitsPerPack?: number
  gtinEan?: string
  primaryFamilyId?: string | null
  requiresColdChain?: boolean
  inUseStabilityMinCold?: number
  inUseStabilityMinRoom?: number
  excursionOutOfColdMaxMin?: number
  prices: CreateProductCatalogPriceDto[]
  notes?: string
  examIds?: number[]
  orderableIfExamAssociated?: boolean
  validation?: CreateProductCatalogValidation
  generatorParentIsotopeId?: string | null
  generatorDaughterIsotopeId?: string | null
}

export interface UpdateProductCatalogDto
  extends Partial<CreateProductCatalogDto> {
  isActive?: boolean
}

export interface ProductFamilyDto {
  id: string
  code?: string
  name: string
  description?: string
  productFamily?: ProductFamilyDto | null
  sortIndex: number
  color?: string
  icon?: string
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface CreateProductFamilyDto {
  code?: string
  name: string
  description?: string
  parentFamilyId?: string | null
  color?: string
  icon?: string
}

export interface UpdateProductFamilyDto
  extends Partial<CreateProductFamilyDto> {
  isActive?: boolean
}

export enum Side {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  MIDLINE = 'MIDLINE',
  NA = 'NA',
}

export interface InjectionSiteDto {
  id: string
  code: string
  bodyPart: string
  side: Side
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface CreateInjectionSiteDto {
  code: string
  bodyPart?: string
  side: Side
}

export interface UpdateInjectionSiteDto
  extends Partial<CreateInjectionSiteDto> {
  isActive?: boolean
}

export enum WasteType {
  SOLID = 'SOLID',
  LIQUID = 'LIQUID',
  SHARPS = 'SHARPS',
}

export enum WasteStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  DISPOSED = 'DISPOSED',
}

export enum ActivityLevel {
  NONE = 'NONE',
  LOW = 'LOW',
  HIGH = 'HIGH',
}

export interface WasteContainerTransferRefDto {
  id: string
  code: string | null
  label: string
  transferredAt: Date | null
}

export interface WasteContainerDto {
  id: string
  no: number
  code: string
  label: string
  type: WasteType
  activityLevel: ActivityLevel
  activityAtRefMbq: number | null
  activityAtTimeMbq: number | null
  startedAt: Date
  closedAt: Date
  disposedAt: Date
  transferredTo: { id: string; code: string | null; label: string } | null
  transferredAt: Date | null
  transferredFrom: WasteContainerTransferRefDto[]
  status: WasteStatus
  storageLocation: StorageLocationDto
  location: string
  notes: string
  tareKg: number
  volumeL: number
  isotopes: IsotopeDto[]
  activeIsotopes: IsotopeDto[]
  wasteEntryCount: number
  logsCount: number
  measurementsCount: number
  isInfectious: boolean
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface WasteContainerLogDto extends EventLogDto {
  wasteEntry: WasteEntryDto | null
}

export interface TransferWasteContainerDto {
  targetContainerId: string
  transferredBy?: number
}

export interface CreateWasteContainerDto {
  code?: string
  label: string
  type: WasteType
  activityLevel: ActivityLevel
  storageLocationId?: string | null
  location?: string
  notes?: string
  tareKg?: number
  volumeL?: number
  isotopeIds?: string[]
  isInfectious?: boolean
}

export interface UpdateWasteContainerDto
  extends Partial<CreateWasteContainerDto> {
  isActive?: boolean
}

export interface CreateWasteContainerMeasurementDto {
  measurementCps?: number
  backgroundNoiseCps?: number
  hotLabEquipmentId?: string
  distanceM?: number
  measuredAt?: Date
  measuredById?: number
}

export interface CloseWasteContainerDto extends CreateWasteContainerMeasurementDto {}
export interface DisposeWasteContainerDto extends CreateWasteContainerMeasurementDto {}
export interface AddWasteContainerMeasurementDto extends CreateWasteContainerMeasurementDto {}

export enum WasteMeasurementType {
  CLOSE = 'CLOSE',
  DISPOSAL = 'DISPOSAL',
  PERIODIC = 'PERIODIC',
}

export interface WasteContainerMeasurementDto {
  id: string
  type: WasteMeasurementType
  measurementCps: number | null
  backgroundNoiseCps: number | null
  distanceM: number | null
  hotLabEquipment: HotLabEquipmentDto | null
  createdAt: Date
  createdBy: MemberDto | null
  measuredBy: MemberDto | null
}

export interface WasteEntryDto {
  id: string
  no: number
  containerId: string
  wasteContainer: WasteContainerDto
  isotope: IsotopeDto | null
  activityAtTimeMbq: number | null
  activityAtRefMbq: number | null
  activityRefAt: Date | null
  volumeMl: number | null
  massG: number | null
  label: string | null
  description: string | null
  createdBy: MemberDto
  collectedBy: MemberDto
  preparationLot: PreparationLotDto
  generatorElution: GeneratorElutionDto
  hotLabEquipment: HotLabEquipmentDto | null
  createdAt: Date
  updatedAt: Date
}

export interface CreateWasteEntryDto {
  containerId: string
  label?: string
  description?: string
  isotopeId?: string
  massG?: number
  volumeMl?: number
  activityAtRefMbq?: number
  activityRefAt?: Date
  collectedById?: number
  fromElutionId?: string
  fromPreparationId?: string
  hotLabEquipmentId?: string
}

export interface ChangeWasteEntryContainerDto {
  targetContainerId: string
}

export enum HotLabEquipmentType {
  DOSE_CALIBRATOR = 'DOSE_CALIBRATOR',
  SURVEY_METER = 'SURVEY_METER',
  GEIGER_COUNTER = 'GEIGER_COUNTER',
  CONTAMINATION_PROBE = 'CONTAMINATION_PROBE',
  LAF = 'LAF',
  FRIDGE = 'FRIDGE',
  FREEZER = 'FREEZER',
  SCALE = 'SCALE',
  ACTIVIMETER = 'ACTIVIMETER',
  PH_METER = 'PH_METER',
  TLC_SYSTEM = 'TLC_SYSTEM',
  TLC_SCANNER = 'TLC_SCANNER',
  GAMMA_SPECTROMETER = 'GAMMA_SPECTROMETER',
  OTHER = 'OTHER',
}

export interface HotLabEquipmentDto {
  id: string
  type: HotLabEquipmentType
  name: string
  hasPeriodicQualityChecks: boolean
  internalCheckIntervalDays: number
  externalCheckIntervalDays: number
  externalOrganization: SupplierDto
  serial: string
  manufacturer: string
  model: string
  installedAt: Date
  location: string
  notes: string
  isActive: boolean
  createdAt: Date
  updatedAt: Date
  storageLocations: StorageLocationDto[]
}

export interface CreateHotLabEquipmentDto {
  type: HotLabEquipmentType
  name: string
  hasPeriodicQualityChecks?: boolean
  internalCheckIntervalDays?: number
  externalCheckIntervalDays?: number
  externalOrganizationId?: string | null
  serial?: string
  manufacturer?: string
  model?: string
  installedAt?: Date
  location?: string
  notes?: string
  storageLocationIds?: string[]
}

export interface UpdateHotLabEquipmentDto
  extends Partial<CreateHotLabEquipmentDto> {
  isActive?: boolean
}

export interface VisualQualityControlDto {
  id: string
  label: string
  type: VisualQualityControlType
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface CreateVisualQualityControlDto {
  label: string
  type: VisualQualityControlType
}

export interface UpdateVisualQualityControlDto
  extends Partial<CreateVisualQualityControlDto> {
  isActive?: boolean
}

export enum VisualQualityControlType {
  APPEARANCE = 'APPEARANCE',
  COLOR = 'COLOR',
}

export interface CustomQualityControlDto {
  id: string
  code: string
  label: string
  type: CustomQualityControlType
  unit: string
  options: CustomQualityControlOptionDto[]
  expectedOption: CustomQualityControlOptionDto
  expectedValue: string
  expected: boolean
  minimum: number | null
  maximum: number | null
  equipmentTypes?: HotLabEquipmentType[]
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface CustomQualityControlOptionDto {
  id: string
  code: string
  label: string
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export enum CustomQualityControlType {
  LIST = 'LIST',
  BOOLEAN = 'BOOLEAN',
  TEXT = 'TEXT',
  NUMERIC = 'NUMERIC',
}

export interface CreateCustomQualityControlDto {
  code: string
  label: string
  type: CustomQualityControlType
  unit?: string
  options?: CreateCustomQualityControlOptionDto[]
  expectedOptionCode?: string
  expectedValue?: string
  expected?: boolean
  minimum?: number
  maximum?: number
  equipmentTypes?: HotLabEquipmentType[]
}

export interface CreateCustomQualityControlOptionDto {
  code: string
  label: string
}

export interface UpdateCustomQualityControlDto
  extends Partial<CreateCustomQualityControlDto> {
  isActive?: boolean
}

export interface UpdateCustomQualityControlOptionDto
  extends Partial<CreateCustomQualityControlOptionDto> {
  isActive?: boolean
}

export enum QCResult {
  PASS = 'PASS',
  FAIL = 'FAIL',
  NA = 'NA',
}

export enum QCType {
  MO_BREAKTHROUGH = 'MO_BREAKTHROUGH',
  AL_BREAKTHROUGH = 'AL_BREAKTHROUGH',
  RADIONUCLIDIC_PURITY = 'RADIONUCLIDIC_PURITY',
  CHEMICAL_PURITY = 'CHEMICAL_PURITY',
  PH = 'PH',
  STERILITY = 'STERILITY',
  ENDOTOXIN = 'ENDOTOXIN',
  VISUAL = 'VISUAL',
  CUSTOM = 'CUSTOM',
  OTHER = 'OTHER',
}

export interface QcTestResultDto {
  id: string
  type: QCType
  result: QCResult
  observedTextValue?: string
  observedNumericValue?: number
  observedBooleanValue?: boolean
  unit?: string
  notes?: string
  attachment?: AttachmentDto
  measuredAt: Date
  measuredBy: MemberDto
  customQualityControl?: CustomQualityControlDto
  visualQualityControl?: VisualQualityControlDto
  qualityControlAssignment: QualityControlAssignmentDto
  createdAt: Date
  createdBy: MemberDto
  invalidationLog?: EventLogDto
  hotLabEquipment?: HotLabEquipmentDto
}

export interface InvalidateQcTestResultDto {
  comment: string
  invalidatedByMemberId: number
}

export type QcEntityType = 'preparationLot' | 'generatorElution'

export interface CreateQcTestResultDto {
  type: QCType
  observedTextValue?: string
  observedNumericValue?: number
  observedBooleanValue?: boolean
  notes?: string
  customQualityControlId?: string
  visualQualityControlId?: string
  measuredAt: Date
  measuredByMemberId?: number
  hotLabEquipmentId?: string
}

export interface QualityControlAssignmentDto {
  id: string
  compound: CompoundDto
  customQualityControls: CustomQualityControlDto[]
  visualQualityControls: VisualQualityControlDto[]
  radiochemicalPurityPercent?: number
  radionuclidicPurityPercent?: number
  phMinimum?: number
  phMaximum?: number
  colorIsRequired: boolean
  appearanceIsRequired: boolean
  radiochemicalPurityIsRequired: boolean
  radionuclidicPurityIsRequired: boolean
  phIsRequired: boolean
  requiredCustomQualityControlIds: string[]
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface QualityControlData {
  assignment: QualityControlAssignmentDto | null
  testResults: QcTestResultDto[]
  canComplete: boolean
  completionLog?: EventLogDto
}

export interface CreateQualityControlAssignmentDto {
  compoundId: string
  customQualityControlIds?: string[]
  colorQualityControlId?: string
  appearanceQualityControlId?: string
  radiochemicalPurityPercent?: number
  radionuclidicPurityPercent?: number
  phMinimum?: number
  phMaximum?: number
  colorIsRequired?: boolean
  appearanceIsRequired?: boolean
  radiochemicalPurityIsRequired?: boolean
  radionuclidicPurityIsRequired?: boolean
  phIsRequired?: boolean
  requiredCustomQualityControlIds?: string[]
}

export interface UpdateQualityControlAssignmentDto
  extends Partial<CreateQualityControlAssignmentDto> {
  isActive?: boolean
}

export interface ActivityLimitDto {
  id: string
  isotope: IsotopeDto
  site: SiteDto
  maxActivityMbq: number
  authorizationNumber: string
  notes: string
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface CreateActivityLimitDto {
  isotopeId: string
  siteId: number
  maxActivityMbq: number
  authorizationNumber?: string
  notes?: string
}

export interface UpdateActivityLimitDto
  extends Partial<CreateActivityLimitDto> {
  isActive?: boolean
}

export interface CompoundProductionStepDto {
  id: string
  code: string
  label: string
  type: CompoundProductionStepType
  unit: string
  expectedValue: string
  expected: boolean
  dilutionVolumeMl?: number | null
  timerDurationSeconds?: number | null
  comment?: string | null
  site: SiteDto
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface CreateCompoundProductionStepDto {
  code: string
  label: string
  type: CompoundProductionStepType
  unit?: string
  expectedValue?: string
  expected?: boolean
  dilutionVolumeMl?: number | null
  timerDurationSeconds?: number | null
  comment?: string | null
  siteId: number
}

export interface UpdateCompoundProductionStepDto
  extends Partial<CreateCompoundProductionStepDto> {
  isActive?: boolean
}

export enum CompoundProductionStepType {
  TEXT = 'TEXT',
  BOOLEAN = 'BOOLEAN',
  DILUTION = 'DILUTION',
  TIMER = 'TIMER',
}

export interface CompoundProductionStepAssignmentDto {
  id: string
  compound: CompoundDto
  steps: CompoundProductionStepDto[]
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface CreateCompoundProductionStepAssignmentDto {
  compoundId: string
  stepIds: string[]
}

export interface UpdateCompoundProductionStepAssignmentDto
  extends Partial<CreateCompoundProductionStepAssignmentDto> {
  isActive?: boolean
}

export interface AttachmentDto {
  id: string
  filename: string
  contentType: string
  url: string
  createdAt: Date
  updatedAt: Date
}

export interface PreparationDocumentDto {
  id: string
  label: string
  compound: CompoundDto
  site: SiteDto
  attachment: AttachmentDto
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface CreatePreparationDocumentDto {
  compoundId: string
  siteId: number
  label: string
  file: File | null
}

export interface UpdatePreparationDocumentDto
  extends Partial<CreatePreparationDocumentDto> {
  isActive?: boolean
}

export interface ActivimeterDetailDto {
  id: string
  equipment: HotLabEquipmentDto
  path?: string | null
  electronicZero?: number | null
  voltageMin?: number | null
  voltageMax?: number | null
  testWithCobalt57: boolean
  testWithCesium137: boolean
  testWithBarium133: boolean
  backgroundWithoutSampleHolderMbq?: number | null
  backgroundWithSampleHolderMbq?: number | null
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface CreateActivimeterDetailDto {
  equipmentId: string
  path?: string | null
  electronicZero?: number | null
  voltageMin?: number | null
  voltageMax?: number | null
  testWithCobalt57?: boolean
  testWithCesium137?: boolean
  testWithBarium133?: boolean
  backgroundWithoutSampleHolderMbq?: number | null
  backgroundWithSampleHolderMbq?: number | null
}

export interface UpdateActivimeterDetailDto
  extends Partial<CreateActivimeterDetailDto> {
  isActive?: boolean
}

export interface ActivimeterCalibrationDto {
  id: string
  equipment: HotLabEquipmentDto
  isotope: IsotopeDto
  value: string
  forbidElutionCreation: boolean
  forbidMrpCreation: boolean
  forbidSyringeCreation: boolean
  forbidSingleDoseCreation: boolean
  forbidInhalationCreation: boolean
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface CreateActivimeterCalibrationDto {
  equipmentId: string
  isotopeId: string
  value: string
  forbidElutionCreation?: boolean
  forbidMrpCreation?: boolean
  forbidSyringeCreation?: boolean
  forbidSingleDoseCreation?: boolean
  forbidInhalationCreation?: boolean
}

export interface UpdateActivimeterCalibrationDto
  extends Partial<CreateActivimeterCalibrationDto> {
  isActive?: boolean
}

export interface EventLogDto {
  id: number
  createdAt: Date
  log?: string | null
  data: any
  event: EventEnum
  compoundProductionStep?: CompoundProductionStepDto
  createdBy: MemberDto
}

export enum EventEnum {
  MEDICAL_ORDER_VIEWED = 'MEDICAL_ORDER_VIEWED',
  REPORT_VIEWED = 'REPORT_VIEWED',
  IMAGES_VIEWED = 'IMAGES_VIEWED',
  REMINDER_NOTIFICATION_SENT = 'REMINDER_NOTIFICATION_SENT',
  MEDICAL_ORDER_UPDATED = 'MEDICAL_ORDER_UPDATED',
  CONFIGURATION_CREATED = 'CONFIGURATION_CREATED',
  CONFIGURATION_UPDATED = 'CONFIGURATION_UPDATED',
  CONFIGURATION_DELETED = 'CONFIGURATION_DELETED',
  PRODUCT_ORDER_CREATED = 'PRODUCT_ORDER_CREATED',
  PRODUCT_ORDER_ACKNOWLEDGED = 'PRODUCT_ORDER_ACKNOWLEDGED',
  PRODUCT_ORDER_CANCELLED = 'PRODUCT_ORDER_CANCELLED',
  PRODUCT_ORDER_COMPLETED = 'PRODUCT_ORDER_COMPLETED',
  PRODUCT_ORDER_RECEIPT_CREATED = 'PRODUCT_ORDER_RECEIPT_CREATED',
  STOCK_TRANSACTION_CREATED = 'STOCK_TRANSACTION_CREATED',
  PRESCRIPTION_CREATED = 'PRESCRIPTION_CREATED',
  PRESCRIPTION_VALIDATED = 'PRESCRIPTION_VALIDATED',
  PRESCRIPTION_INVALIDATED = 'PRESCRIPTION_INVALIDATED',
  PREPARATION_LOT_STEP_VALIDATED = 'PREPARATION_LOT_STEP_VALIDATED',
  ORDER_RECIPIENT_ADDED = 'ORDER_RECIPIENT_ADDED',
  ORDER_RECIPIENT_REMOVED = 'ORDER_RECIPIENT_REMOVED',
  ORDER_DOCUMENT_ADDED = 'ORDER_DOCUMENT_ADDED',
  ORDER_DOCUMENT_REMOVED = 'ORDER_DOCUMENT_REMOVED',
  ORDER_UPDATED = 'ORDER_UPDATED',
  ORDER_COUVERTURE_UPDATED = 'ORDER_COUVERTURE_UPDATED',
  ORDER_PATIENT_UPDATED = 'ORDER_PATIENT_UPDATED',
  ORDER_TRANSPORT_CREATED = 'ORDER_TRANSPORT_CREATED',
  ORDER_TRANSPORT_UPDATED = 'ORDER_TRANSPORT_UPDATED',
  ORDER_PRECOTATION_ADDED = 'ORDER_PRECOTATION_ADDED',
  ORDER_PRECOTATION_REMOVED = 'ORDER_PRECOTATION_REMOVED',
  WASTE_ENTRY_CREATED = 'WASTE_ENTRY_CREATED',
  WASTE_ENTRY_COLLECTED = 'WASTE_ENTRY_COLLECTED',
  WASTE_ENTRY_CANCELLED = 'WASTE_ENTRY_CANCELLED',
  WASTE_ENTRY_CONTAINER_CHANGED = 'WASTE_ENTRY_CONTAINER_CHANGED',
  WASTE_ENTRY_CONTAINER_CHANGED_RECEIVED = 'WASTE_ENTRY_CONTAINER_CHANGED_RECEIVED',
  WASTE_CONTAINER_CLOSED = 'WASTE_CONTAINER_CLOSED',
  WASTE_CONTAINER_REOPENED = 'WASTE_CONTAINER_REOPENED',
  WASTE_CONTAINER_DISPOSED = 'WASTE_CONTAINER_DISPOSED',
  WASTE_CONTAINER_DISPOSE_CANCELLED = 'WASTE_CONTAINER_DISPOSE_CANCELLED',
  WASTE_CONTAINER_TRANSFERRED = 'WASTE_CONTAINER_TRANSFERRED',
  WASTE_CONTAINER_TRANSFER_CREATED = 'WASTE_CONTAINER_TRANSFER_CREATED',
  WASTE_CONTAINER_TRANSFER_CANCELLED = 'WASTE_CONTAINER_TRANSFER_CANCELLED',
  WASTE_CONTAINER_TRANSFER_RECEIVED = 'WASTE_CONTAINER_TRANSFER_RECEIVED',
  WASTE_CONTAINER_TRANSFER_RECEIVED_CANCELLED = 'WASTE_CONTAINER_TRANSFER_RECEIVED_CANCELLED',
  WASTE_CONTAINER_MEASUREMENT_MEASURED = 'WASTE_CONTAINER_MEASUREMENT_MEASURED',
  WASTE_CONTAINER_MEASUREMENT_CREATED = 'WASTE_CONTAINER_MEASUREMENT_CREATED'
}

export interface ProductOrderLineItemDto {
  product: ProductCatalogDto
  quantity: number
  remainingQuantity?: number
  unitPriceExclVat: number
  vatRate: number
  desiredDeliveryDate?: Date
  gePurchaseOrderAttachment?: AttachmentDto
  plannedDates?: { date: Date; quantity: number }[]
}

export enum ProductOrderStatus {
  PENDING_VALIDATION = 'PENDING_VALIDATION',
  REJECTED = 'REJECTED',
  PENDING_MAIL = 'PENDING_MAIL',
  MAIL_FAILED = 'MAIL_FAILED',
  PENDING_ORDER_ACKNOWLEDGMENT = 'PENDING_ORDER_ACKNOWLEDGMENT',
  PENDING_DELIVERY = 'PENDING_DELIVERY',
  PARTIALLY_RECEIVED = 'PARTIALLY_RECEIVED',
  RECEIVED = 'RECEIVED',
  RECEIVED_WITH_DISCREPANCIES = 'RECEIVED_WITH_DISCREPANCIES',
  CANCELLED = 'CANCELLED',
  PENDING_CANCELLATION_MAIL = 'PENDING_CANCELLATION_MAIL',
  CANCELLATION_MAIL_FAILED = 'CANCELLATION_MAIL_FAILED',
  PENDING_CREDIT_NOTE_MAIL = 'PENDING_CREDIT_NOTE_MAIL',
  CREDIT_NOTE_MAIL_FAILED = 'CREDIT_NOTE_MAIL_FAILED',
}

export enum ProductOrderCancellationReason {
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  ORDER_DUPLICATE = 'ORDER_DUPLICATE',
  OTHER = 'OTHER',
}

export enum CreditNoteProposalStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REFUSED = 'REFUSED'
}

export enum ProductOrderPaymentStatus {
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  PAID = 'PAID',
}

export interface ProductOrderPaymentDto {
  id: string
  paymentDate: Date
  attachment?: AttachmentDto
  createdAt: Date
}

export interface CreateProductOrderPaymentDto {
  paymentDate: string
  markAsPaid: boolean
  file?: File | null
}

export interface ProductOrderDto {
  id: string
  createdBy: MemberDto
  cancelledBy: MemberDto | null
  completedBy: MemberDto | null
  rejectedAt?: Date
  rejectedBy?: MemberDto
  internalOrderNumber: string
  supplierOrderNumber?: string
  supplier: SupplierDto
  supplierEmail: string
  lineItems: ProductOrderLineItemDto[]
  acknowledgmentAttachments?: AttachmentDto[]
  amountExclVat: number
  amountInclVat: number
  extraCost?: number
  deliveryInstructions?: string
  site: SiteDto
  status: ProductOrderStatus
  streetAddress: string
  postalCode: number
  city: string
  country: string
  phoneNumber: string
  fax?: string
  cancellationEmail?: string
  cancellationComment?: string
  cancellationReason?: ProductOrderCancellationReason
  cancelledAt?: Date
  createdAt: Date
  updatedAt: Date
  sentToSupplierAt?: Date
  creditNoteProposalStatus?: CreditNoteProposalStatus
  creditNoteProposalAt?: Date
  creditNoteEmails?: string[]
  creditNoteSentAt?: Date
  paymentStatus?: ProductOrderPaymentStatus
  payments?: ProductOrderPaymentDto[]
}

export interface CreateProductOrderLineItemPlannedDatesDto {
  date: string
  quantity: number
}

export interface CreateProductOrderLineItemDto {
  productId: string
  quantity: number | null
  desiredDeliveryDate?: string
  isStandardDelay?: boolean
  hasGePurchaseOrderAttachment?: boolean // not used for backend call
  plannedDates?: CreateProductOrderLineItemPlannedDatesDto[]
}

export interface CreateProductOrderDto {
  supplierId: string
  supplierContactId: string
  lineItems: CreateProductOrderLineItemDto[]
  extraCost?: number
  deliveryInstructions?: string
  siteId: number
  shippingAddressId: number
}

export interface CreateProductOrderAcknowledgmentDto {
  file: File
  supplierOrderNumber?: string
}

export interface CancelProductOrderDto {
  supplierContactId?: string
  reason: ProductOrderCancellationReason
  comment?: string
}

export interface StockDto {
  genericName: string
  units: number
  examUnits: number
  isAlertStock: boolean
}

export interface ProductBatchDto {
  id: string
  totalUnits: number
  storageLocations: {
    storageLocation: StorageLocationDto
    units: number
  }[]
  productCatalog: ProductCatalogDto
  lotNumber: string
  receivedAt: Date
  expiryAt: Date
  activityAtRefMbq: number
  activityRefAt: Date
  productionSite: string
  volumeMl: number
  certificateUrl?: string
  storageCondition: string
  receivedByExternal: string
  notes?: string
  createdAt: Date
  updatedAt: Date
}

export enum StockReason {
  RECEIPT = 'RECEIPT',
  CONSUMPTION = 'CONSUMPTION',
  TRANSFER_IN = 'TRANSFER_IN',
  TRANSFER_OUT = 'TRANSFER_OUT',
  ADJUSTMENT_POSITIVE = 'ADJUSTMENT_POSITIVE',
  ADJUSTMENT_NEGATIVE = 'ADJUSTMENT_NEGATIVE',
  WASTE = 'WASTE',
  RETURN_TO_SUPPLIER = 'RETURN_TO_SUPPLIER',
  INVENTORY_COUNT = 'INVENTORY_COUNT',
  EXPIRATION = 'EXPIRATION',
}

export interface StockTransactionDto {
  id: string
  storageLocation: StorageLocationDto
  productBatch: ProductBatchDto
  occurredAt: Date
  reason: StockReason
  unitsDelta: number
  volumeMlDelta: number
  scannedBarcode?: string
  reference?: string
  notes?: string
  createdAt: Date
  updatedAt: Date
  createdBy: MemberDto
}

export interface CreateStockTransactionDto {
  storageLocationId: string | null
  targetStorageLocationId?: string | null // for TRANSFER_OUT reason
  productBatchId: string
  reason: StockReason
  unitsDelta?: number
  volumeMlDelta?: number
  scannedBarcode?: string
  reference?: string
  notes?: string
}

export interface StockAlertDto {
  id: string
  productGenericName: string
  thresholdUnits: number
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface CreateStockAlertDto {
  productGenericName: string
  thresholdUnits: number
}

export interface UpdateStockAlertDto extends Partial<CreateStockAlertDto> {
  isActive?: boolean
}

export interface ProductOrderReceiptNewProductDto {
  baseProduct: ProductCatalogDto
  product: ProductCatalogDto
}

export interface ProductOrderReceiptDto {
  id: string
  order: ProductOrderDto
  createdBy: MemberDto
  receivedAt: Date
  receivedByMember: MemberDto
  externalSupplierName?: string
  externalSupplierAddress?: string
  courierFullName?: string
  comment?: string
  errorComment?: string
  deliveryNote?: AttachmentDto
  releaseCertificate?: AttachmentDto
  stockTransactions: StockTransactionDto[]
  newProducts: ProductOrderReceiptNewProductDto[]
  createdAt: Date
}

export interface CreateProductOrderReceiptLineItemDto {
  productId: string
  receivedUnits: number
  storageLocationId: string
  lotNumber: string
  productionSite?: string
  activityAtRefMbq?: number
  activityRefAt?: Date
  expiryAt?: Date
}

export interface CreateProductOrderReceiptNewProductDto extends CreateProductOrderReceiptLineItemDto {
  baseProductId: string
}

export interface CreateProductOrderReceiptDto {
  orderId: string
  receivedByMemberId?: number
  receivedAt?: Date
  courierFullName?: string
  useOrderSupplier?: boolean // not used for backend call
  useConnectedMemberAsReceiver?: boolean // not used for backend call
  externalSupplierName?: string
  externalSupplierAddress?: string
  comment?: string
  errorComment?: string
  lineItems: CreateProductOrderReceiptLineItemDto[]
  newProducts: CreateProductOrderReceiptNewProductDto[]
  markOrderAsReceived?: boolean
}

export interface GeneratorElutionInstanceDto {
  id: string
  generatorInstance: GeneratorInstanceDto
  volumeMl: number
  activityAtRefMbq: number
  activityRefAt: Date
  theoreticalActivityAtTimeMbq?: number | null
}

export interface GeneratorElutionDto {
  id: string
  no: number
  isotope: IsotopeDto
  volumeMl: number
  remainingVolumeMl: number
  activityAtRefMbq: number
  activityAtTimeMbq: number | null
  activityRefAt: Date
  notes?: string
  qualityControlStatus: QualityControlStatus
  qualityControlDone?: number
  qualityControlTotal?: number
  generatorElutionInstances: GeneratorElutionInstanceDto[]
  theoreticalActivityAtTimeMbq?: number | null
  yieldPercent?: number | null
  createdBy: MemberDto
  performedBy: MemberDto
  createdAt: Date
  wasteEntry?: WasteEntryDto
}

export interface CreateGeneratorElutionInstanceDto {
  generatorInstanceId: string
  volumeMl: number
  activityAtRefMbq: number
  activityRefAt?: Date
}

export interface CreateGeneratorElutionDto {
  generatorInstances: CreateGeneratorElutionInstanceDto[]
  notes?: string
  performedById?: number
}

export interface GeneratorInstanceDto {
  id: string
  productBatch: ProductBatchDto
  activityAtRefGbq: number
  activityRefAt: Date
  serialNumber: string
  lastElutionDate?: Date | null
  theoreticalActivityAtTimeMbq?: number | null
}

export interface GeneratorSlotDto {
  id: string
  name: string
  generatorInstance?: GeneratorInstanceDto | null
}

export interface CreateGeneratorSlotDto {
  name: string
}

export interface UpdateGeneratorSlotDto {
  id?: string
  name: string
}

export interface WorkbenchDto {
  id: string
  code: string
  name: string
  hotLabEquipments: HotLabEquipmentDto[]
  wasteContainers: WasteContainerDto[]
  room: LabWorkstation
  generatorSlots: GeneratorSlotDto[]
}

export interface CreateWorkbenchDto {
  code: string
  name: string
  hotLabEquipmentIds: string[]
  wasteContainerIds: string[]
  roomId: number
  generatorSlots: CreateGeneratorSlotDto[]
}

export interface UpdateWorkbenchDto extends Omit<Partial<CreateWorkbenchDto>, 'generatorSlots'> {
  isActive?: boolean
  generatorSlots?: UpdateGeneratorSlotDto[]
}

export interface CreditNoteChoiceDto {
  choice: CreditNoteProposalStatus
  supplierContactIds?: string[]
}

export interface UpsertWorkbenchGeneratorInstanceDto {
  generatorSlotId?: string
  generatorInstanceId?: string
  productBatchId?: string
  storageLocationId?: string
  serialNumber?: string
  activityAtRefGbq?: number // required
  activityRefAt?: Date // required
}

export interface SubmittedUpsertWorkbenchGeneratorInstanceDto extends Omit<UpsertWorkbenchGeneratorInstanceDto, 'activityAtRefGbq'> {
  activityAtRefMbq: number
}

export interface AppointmentDto {
  id: number
  start: Date
  end: Date
  examRoomId: number
  medicalOrder: MedicalOrder
  appointmentNotes?: string
  patientNotes?: string
  waitingList: boolean
  isEmergency: boolean
  hospitalizedPatient: boolean
  attachments: GedFileDto[]
  patient: Partial<PatientDto>
  referringDoctorRpps?: string
  familyDoctorRpps?: string
  exam: Exam
  attendingDoctor: {
    id: number
    firstName: string
    lastName: string
  }
  examRoom: ExamRoom
  weightKg?: number
  heightCm?: number
  familyDoctor?: Partial<DoctorDto>
  referringDoctor?: Partial<DoctorDto>
  createdBy?: AccountDto
  createdAt: Date
  updatedAt: Date
  deletedAt?: Date
}

export enum PrescriptionStatus {
  PENDING = 'PENDING',
  VALIDATED = 'VALIDATED',
}

export interface PosologySliceDto {
  id: string
  weightMin: number | null
  weightMax: number | null
  type: PosologySliceType
  patientType: PosologyPatientType
  activityMBq: number
  eanmBaseActivityMBq: number | null // EANM child case
  eanmMinActivityMBq: number | null // EANM child case
  eanmClass: EANMClass | null // EANM child case
}

export interface CreatePosologySliceDto {
  weightMin?: number | null
  weightMax?: number | null
  type: PosologySliceType
  patientType: PosologyPatientType
  activityMBq?: number | null
  eanmBaseActivityMBq?: number | null
  eanmMinActivityMBq?: number | null
  eanmClass?: EANMClass | null
}

export interface CreateExamCompoundDto {
  compoundId: string
  posologySlices: CreatePosologySliceDto[]
}

export interface CreateExamFormValues {
  code: string
  label: string
  modality?: string
  procedureTime?: number
  sex?: SexEnum
  bodyPart?: BodyPartEnum
  ageRange?: AgeRangeEnum
  isNuclear?: boolean
  instruction?: string
  dose?: boolean
  contrast?: boolean
  shower?: boolean
  fasted?: boolean
  medication?: boolean
  wc?: boolean
  siteId: number
  instructionsList?: string[]
  cotation_ccam: string[]
  cotation_ngap: string[]
  examCompounds?: CreateExamCompoundDto[]
  modaliteId?: number | null
  dmpDelayInHours?: number
}

export interface PrepRoomFormValues {
  code: string
  label: string
  examRoomId: number
  isWheelchairCompatible: boolean
  siteId: number
  locationInstructions?: string
}

export interface ExamCompoundDto {
  id: string
  compound: CompoundDto
  posologySlices: PosologySliceDto[]
}

export enum PosologySliceType {
  MBQ_PER_KG = 'MBQ_PER_KG',
  MBQ_FIXED = 'MBQ_FIXED',
  EANM = 'EANM'
}

export enum PosologyPatientType {
  ADULT = 'ADULT',
  CHILD = 'CHILD',
  PREGNANT = 'PREGNANT'
}

export enum EANMClass {
  A = 'A',
  B = 'B',
  C = 'C'
}

export interface PrescriptionDto {
  id: string
  status: PrescriptionStatus
  appointment: Partial<AppointmentDto>
  activityMBq?: number
  recommendedActivityMBq?: number
  compound: CompoundDto
  createdBy?: MemberDto
  notes?: string
  validationHistory?: EventLogDto[]
  childWithAdultWeight?: boolean
}

export interface ValidatePrescriptionDto {
  activityMBq: number
  notes?: string
}

export enum IsotopeUsage {
  DIAGNOSTIC = 'DIAGNOSTIC',
  TREATMENT = 'TREATMENT',
}

export enum IsotopeEmissionType {
  GAMMA = 'GAMMA',
  BETA_PLUS = 'BETA_PLUS',
  BETA_MINUS = 'BETA_MINUS',
  ALPHA = 'ALPHA',
  ELECTRON_CAPTURE = 'ELECTRON_CAPTURE',
}

export enum PreparationStatus {
  DRAFT = 'DRAFT',
  READY = 'READY',
  RELEASED = 'RELEASED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
}

export enum QualityControlStatus {
  NONE = 'NONE',
  IN_PROGRESS = 'IN_PROGRESS',
  NON_COMPLIANT = 'NON_COMPLIANT',
  COMPLETE = 'COMPLETE',
}

export interface AvailableElutionDto extends GeneratorElutionDto {
  remainingVolumeMl: number
}

export interface PreparationLotItemDto {
  elutionId: string
  activityAtRefMbq: number | null
  activityRefAt: Date | null
  elutionVolumeMl: number | null
}

export interface PreparationLotDto {
  id: string
  no: number
  compound: CompoundDto
  sourceElutions: GeneratorElutionDto[]
  sourceElutionItems: PreparationLotItemDto[]
  sourceBatch?: ProductBatchDto | null
  sourceBatchStorageLocation?: StorageLocationDto | null
  cerclageColor?: string | null
  notes?: string | null
  status: PreparationStatus
  qualityControlStatus: QualityControlStatus
  qualityControlDone?: number
  qualityControlTotal?: number
  preparedAt: Date
  activityAtRefMbq: number
  activityRefAt: Date
  totalVolumeMl?: number | null
  elutionVolumeMl?: number | null
  dilutionVolumeMl?: number | null
  currentStepIndex?: number | null
  activityAtTimeMbq: number | null
  activeStepTimer?: PreparationLotStepTimerDto | null
  performedBy?: MemberDto | null
  createdBy?: MemberDto | null
  createdAt: Date
  updatedAt: Date
  stepLogs?: EventLogDto[]
  wasteEntry?: WasteEntryDto
}

export interface CreatePreparationLotItemDto {
  elutionId: string
  activityAtRefMbq?: number
  elutionVolumeMl?: number
  activityRefAt?: Date
}

export interface CreatePreparationLotDto {
  compoundId: string
  sourceElutionItems: CreatePreparationLotItemDto[]
  cerclageColor?: string
  notes?: string
  performedById?: number
  sourceBatchId?: string
  storageLocationId?: string
}

export interface UpdatePreparationLotDto {
  currentStepIndex?: number | null
  status?: PreparationStatus
}

export interface PreparationLotStepTimerDto {
  id: string
  preparationLotId: string
  stepIndex: number
  durationSeconds: number
  startedAt: string
  completedAt?: string | null
}

export interface StartPreparationLotStepTimerDto {
  stepIndex: number
  durationSeconds: number
}
